import { inject, Injectable } from '@angular/core';
import { ADD_CAMERA_TIMEOUT, ADD_CAMERA_STATUS_TEXTS } from '@hunting-nature/Secacam/pages/add-camera-flow/add-camera/add-camera.config';
import { ToastService } from '@hunting-nature/Secacam/shared/services/toast.service';
import { ADD_CAMERA_ERROR } from '@hunting-nature/shared-core/enums/add-camera-error.enum';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { getFullRoutePath, getStepByPosition } from '@hunting-nature/shared-core/step-progress';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  tap,
  TimeoutError,
  timer,
  withLatestFrom,
} from 'rxjs';
import { timeout } from 'rxjs/operators';

import { AppConfigLoader } from '../../../config/app-config.loader';
import { CamerasPageService } from '../../cameras/services/cameras.page.service';
import { RemoteControlService } from '../../cameras/services/remote-control.service';
import { ADD_CAMERA_FLOW_CONFIG } from '../shared/add-camera-flow-progress.config';
import { AddCameraService } from '../shared/add-camera.service';

import { AddCameraFlowActions } from './add-camera-flow.actions';
import { AddCameraFlowSelectors } from './add-camera-flow.reducer';
import { CameraModelService } from '@hunting-nature/shared-core/services/camera-model.service';

@Injectable()
export class AddCameraFlowEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly appConfigLoader = inject(AppConfigLoader);
  private readonly addCameraService = inject(AddCameraService);
  private readonly camerasPageService = inject(CamerasPageService);
  private readonly navController = inject(NavController);
  private readonly remoteControlService = inject(RemoteControlService);
  private readonly cameraModelService = inject(CameraModelService);
  private readonly toastService = inject(ToastService);

  scanActivationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.scanActivationCode),
      switchMap(async () => {
        const activationCode = await this.addCameraService.scanActivationCode();
        if (activationCode) {
          return AddCameraFlowActions.scanActivationCodeSucceeded({ activationCode });
        }
        return AddCameraFlowActions.scanActivationCodeFailed();
      })
      // TODO: handle errors properly, e.g. show alert if camera permission is denied
      //catchErrror()
    )
  );

  addCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.addCamera),
      switchMap(action =>
        this.addCameraService.activateCamera$(action.activationCode).pipe(
          timeout(ADD_CAMERA_TIMEOUT),
          map(camera => AddCameraFlowActions.addCameraSucceeded({ camera })),
          catchError((error: unknown) => of(AddCameraFlowActions.addCameraFailed({ error: this.mapResponseToError(error) })))
        )
      )
    )
  );

  addCameraStatusText$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.addCamera),

      switchMap(() => {
        const stop$ = this.actions$.pipe(ofType(AddCameraFlowActions.addCameraSucceeded, AddCameraFlowActions.addCameraFailed));

        return timer(0, 5000).pipe(
          map(index => AddCameraFlowActions.addCameraChangeStatusText({ index: 1 + (index % (ADD_CAMERA_STATUS_TEXTS.length - 1)) })),

          takeUntil(stop$)
        );
      })
    )
  );

  addCameraFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.addCameraFailed),
        tap(action => {
          if (action.error === ADD_CAMERA_ERROR.TIMEOUT_ERROR) {
            void this.toastService.showTranslatedError$('addCameraFlow.addCamera.form.timeout');
          }
        })
      ),
    { dispatch: false }
  );

  openAddCameraFlow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.openAddCameraFlow),
        tap(() => {
          const firstStep = getStepByPosition(ADD_CAMERA_FLOW_CONFIG, 1);
          if (!firstStep) {
            console.error('Could not find first step in add camera flow');
            return;
          }

          void this.navController.navigateForward([getFullRoutePath(ADD_CAMERA_FLOW_CONFIG, firstStep)]);
        })
      ),
    { dispatch: false }
  );

  closeAddCameraFlow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.closeAddCameraFlow),
        tap(() => void this.navController.navigateRoot(['/home/cameras']))
      ),
    { dispatch: false }
  );

  goToTestConnectionStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.goToTestConnectionStep),
        tap(() => void this.navController.navigateForward('/add-camera-flow/test-connection'))
      ),
    { dispatch: false }
  );

  goBackFromTestConnectionStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.goBackFromTestConnectionStep),
        tap(() => void this.navController.navigateBack('/add-camera-flow/add-camera'))
      ),
    { dispatch: false }
  );

  goToGpsSetupStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.goToGpsSetupStep),
        withLatestFrom(this.store.select(AddCameraFlowSelectors.currentStep)),
        tap(
          ([_, currentStep]) =>
            void this.navController.navigateForward([getFullRoutePath(ADD_CAMERA_FLOW_CONFIG, getStepByPosition(ADD_CAMERA_FLOW_CONFIG, currentStep)!)])
        )
      ),
    { dispatch: false }
  );

  goBackFromGpsSetupStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.goBackFromGpsSetupStep),
        tap(() => void this.navController.navigateBack('/add-camera-flow/test-connection'))
      ),
    { dispatch: false }
  );

  goToSetupCompleteStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.goToSetupCompleteStep),
        tap(() => void this.navController.navigateForward('/add-camera-flow/setup-complete'))
      ),
    { dispatch: false }
  );

  goBackFromSetupCompleteStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddCameraFlowActions.goBackFromSetupCompleteStep),
        withLatestFrom(this.store.select(AddCameraFlowSelectors.currentStep)),
        tap(
          ([_, currentStep]) =>
            void this.navController.navigateForward([getFullRoutePath(ADD_CAMERA_FLOW_CONFIG, getStepByPosition(ADD_CAMERA_FLOW_CONFIG, currentStep)!)])
        )
      ),
    { dispatch: false }
  );

  activateCameraGps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.activateGpsForAllCameras),
      map(action => this.remoteControlService.applyGpsAndGpsPulsForAllCameras(action.activatedCameras)),
      mergeMap(requests =>
        forkJoin(requests).pipe(
          map(() => AddCameraFlowActions.activateCameraGpsSucceeded()),
          catchError((error: unknown) => of(AddCameraFlowActions.activateCameraGpsFailed({ error: error as Error })))
        )
      )
    )
  );

  requestTestImagesForNewCameras$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.requestTestImageForAllNewCameras),
      mergeMap(({ cameraIds }) => from(cameraIds).pipe(map(cameraId => AddCameraFlowActions.requestTestImageForNewCamera({ cameraId }))))
    )
  );

  requestTestImagesForNewCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.requestTestImageForNewCamera),
      mergeMap(({ cameraId }) =>
        this.camerasPageService.getCameraById$(cameraId).pipe(
          map((camera: Camera) => {
            if (camera.image?.link) {
              return AddCameraFlowActions.requestTestImageForNewCameraSuccess({ cameraId, imageUrl: camera.image.link });
            }

            const maximumNumberOfRetries: number = this.appConfigLoader.activeConfig().testImage.maximumNumberOfRetries;
            return AddCameraFlowActions.requestTestImageForNewCameraFailed({ cameraId, maximumNumberOfRetries });
          }),

          catchError(() => {
            const maximumNumberOfRetries: number = this.appConfigLoader.activeConfig().testImage.maximumNumberOfRetries;
            return of(AddCameraFlowActions.requestTestImageForNewCameraFailed({ cameraId, maximumNumberOfRetries }));
          })
        )
      )
    )
  );

  requestTestImagesForNewCameraFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.requestTestImageForNewCameraFailed),
      withLatestFrom(this.store.select(AddCameraFlowSelectors.testImageStatuses)),
      filter(([{ cameraId }, testImages]) => {
        const testImageStatus = testImages[cameraId];

        return testImageStatus && !testImageStatus.finished;
      }),
      map(([{ cameraId }]) => AddCameraFlowActions.scheduleRetryRequestTestImageForNewCamera({ cameraId }))
    )
  );

  scheduleRetryRequestTestImagesForNewCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.scheduleRetryRequestTestImageForNewCamera),

      mergeMap(({ cameraId }) =>
        timer(this.appConfigLoader.activeConfig().testImage.waitBetweenTestImageQueriesInMs).pipe(
          map(() => AddCameraFlowActions.retryNowRequestTestImageForNewCamera({ cameraId })),
          takeUntil(this.actions$.pipe(ofType(AddCameraFlowActions.goBackFromTestConnectionStep, AddCameraFlowActions.goToGpsSetupStep)))
        )
      )
    )
  );

  retryNowRequestTestImagesForNewCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.retryNowRequestTestImageForNewCamera),
      map(({ cameraId }) => AddCameraFlowActions.requestTestImageForNewCamera({ cameraId }))
    )
  );

  restartRequestTestImageForNewCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.restartRequestTestImageForNewCamera),
      map(({ cameraId }) => AddCameraFlowActions.requestTestImageForNewCamera({ cameraId }))
    )
  );

  requestTestImageForAllNewCamerasFinished$ = createEffect(() =>
    this.store.select(AddCameraFlowSelectors.isAllTestImageFinished).pipe(
      distinctUntilChanged(),
      withLatestFrom(this.store.select(AddCameraFlowSelectors.isTestImagesQueryStarted)),
      filter(([isAllTestImageFinished, isTestImagesQueryStarted]) => isAllTestImageFinished && isTestImagesQueryStarted),
      map(() => AddCameraFlowActions.requestTestImageForAllNewCamerasFinished())
    )
  );

  addCameraFlowEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCameraFlowActions.addCameraFlowEntered),
      switchMap(() =>
        this.cameraModelService.fetchCameraModels$().pipe(
          map(cameraModels => AddCameraFlowActions.addCameraFlowEnteredSucceeded({ cameraModels })),
          catchError((error: unknown) => of(AddCameraFlowActions.addCameraFlowEnteredFailed({ error: error as Error })))
        )
      )
    )
  );

  private mapResponseToError(error: unknown): ADD_CAMERA_ERROR {
    if (error instanceof TimeoutError) {
      return ADD_CAMERA_ERROR.TIMEOUT_ERROR;
    }

    if (typeof error === 'object' && error !== null && 'status' in error && (error as { status: number }).status === 400) {
      return ADD_CAMERA_ERROR.CODE_USED_OR_INVALID;
    }

    return ADD_CAMERA_ERROR.GENERIC;
  }
}
